import * as turf from "@turf/turf";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

let currentMeasureModeState = null;

const BaseMode = MapboxDraw.modes.draw_line_string;

const MeasureMode = {
  ...BaseMode,

  onSetup() {
    this.map.getCanvas().style.cursor = "crosshair";

    const state = {
      lines: [],
      textFeatures: [],
      currentLine: null,
      currentTextFeature: null,
      currentVertexPosition: 0,
      unit: this.unit || "meters",
    };

    currentMeasureModeState = state;

    return state;
  },
  onClick(state, e) {
    const coords = [e.lngLat.lng, e.lngLat.lat];

    if (!state.currentLine) {
      const line = this.newFeature({
        type: "Feature",
        properties: { isMeasureLine: true },
        geometry: { type: "LineString", coordinates: [coords, coords] },
      });

      this.addFeature(line);
      state.currentLine = line;
      state.currentVertexPosition = 1;
    } else {
      state.currentLine.updateCoordinate(state.currentVertexPosition, ...coords);
      state.currentVertexPosition++;
      this.finalizeMeasurement(state);
    }
  },

  onMouseMove(state, e) {
    if (!state.currentLine) return;

    const coords = [e.lngLat.lng, e.lngLat.lat];
    state.currentLine.updateCoordinate(state.currentVertexPosition, ...coords);
    this.updateMeasurement(state);
  },

  setUnit(newUnit, map) {
    if (!currentMeasureModeState) return;

    currentMeasureModeState.unit = newUnit;
    currentMeasureModeState.textFeatures = [];

    currentMeasureModeState.lines.forEach((line) => {
      const geo = line.toGeoJSON();
      const length = turf.length(geo, { units: newUnit });

      const coords = geo.geometry.coordinates;
      const midpoint = turf.midpoint(turf.point(coords[0]), turf.point(coords[coords.length - 1]));

      currentMeasureModeState.textFeatures.push({
        type: "Feature",
        properties: { dimension: `${length.toFixed(2)} ${newUnit === "meters" ? "m" : "ft"}` },
        geometry: { type: "Point", coordinates: midpoint.geometry.coordinates },
      });
    });

    const updatedTextFeatures = JSON.parse(JSON.stringify(currentMeasureModeState.textFeatures));
    const source = map.getSource("measure-text-layer");

    if (source) {
      source.setData({
        type: "FeatureCollection",
        features: updatedTextFeatures,
      });
    }

    map.fire("draw.update", {
      features: currentMeasureModeState.lines.map((line) => line.toGeoJSON()),
    });
  },

  updateMeasurement(state) {
    const lineGeoJSON = state.currentLine.toGeoJSON();
    const unit = state.unit || "meters";

    const length = turf.length(lineGeoJSON, { units: unit });
    const coords = lineGeoJSON.geometry.coordinates;
    const midpoint = turf.midpoint(turf.point(coords[0]), turf.point(coords[coords.length - 1]));

    const displayUnit = unit === "feet" ? "ft" : "m";

    state.currentTextFeature = {
      type: "Feature",
      properties: {
        dimension: `${length.toFixed(2)} ${displayUnit}`,
      },
      geometry: { type: "Point", coordinates: midpoint.geometry.coordinates },
    };

    this.updateTextLayer(state);
  },

  getUnit() {
    return currentMeasureModeState?.unit || "meters";
  },
  // updateMeasurement(state) {
  //   const lineGeoJSON = state.currentLine.toGeoJSON();
  //   const length = turf.length(lineGeoJSON, { units: "meters" });

  //   const coords = lineGeoJSON.geometry.coordinates;
  //   const midpoint = turf.midpoint(turf.point(coords[0]), turf.point(coords[coords.length - 1]));

  //   state.currentTextFeature = {
  //     type: "Feature",
  //     properties: { dimension: `${length.toFixed(2)} m` },
  //     geometry: { type: "Point", coordinates: midpoint.geometry.coordinates },
  //   };

  //   this.updateTextLayer(state);
  // },

  finalizeMeasurement(state) {
    state.lines.push(state.currentLine);
    state.textFeatures.push(state.currentTextFeature);

    state.currentLine = null;
    state.currentTextFeature = null;

    this.updateTextLayer(state);
  },

  updateTextLayer(state) {
    const source = this.map.getSource("measure-text-layer");

    if (!source) {
      this.map.addSource("measure-text-layer", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      this.map.addLayer({
        id: "measure-text-layer",
        type: "symbol",
        source: "measure-text-layer",
        layout: {
          "text-field": ["get", "dimension"],
          "text-size": 16,
          "text-font": ["Open Sans Bold"],
          "text-offset": [0, 1.2],
          "text-allow-overlap": true,
          "text-keep-upright": true,
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });
    }

    this.map.getSource("measure-text-layer").setData({
      type: "FeatureCollection",
      features: [...state.textFeatures, ...(state.currentTextFeature ? [state.currentTextFeature] : [])],
    });

    this.map.fire("draw.update", { features: state.lines.map((line) => line.toGeoJSON()) });
  },

  cleanup(map) {
    if (map.getLayer("measure-text-layer")) {
      map.removeLayer("measure-text-layer");
    }

    if (map.getSource("measure-text-layer")) {
      map.removeSource("measure-text-layer");
    }

    currentMeasureModeState = null;
    this._state = null;
    this.map = null;
  },

  toDisplayFeatures(state, geojson, display) {
    display(geojson);

    state.lines.forEach((line) => display(line.toGeoJSON()));
  },

  onStop(state) {
    if (state.currentLine) {
      this.deleteFeature(state.currentLine.id, { silent: true });
      state.currentLine = null;
    }
    state.currentTextFeature = null;
    state.lines = [];
    state.textFeatures = [];
  },

  clearMeasurements() {
    const map = this.map;
    if (map.getSource("measure-text-layer")) {
      map.getSource("measure-text-layer").setData({ type: "FeatureCollection", features: [] });
    }

    if (this.lines) {
      this.lines.forEach((line) => this.deleteFeature(line.id, { silent: true }));
    }

    this.lines = [];
    this.textFeatures = [];
  },
};

export { MeasureMode };
